import { createSelector, Selector } from '@ngxs/store';
import { UserState, type UserStateModel } from './user.state';
import { type UserRole, UserRoles } from '@szebit-app/sdk/backend';

export class UserStateSelectors {
  @Selector([UserState])
  static user({ user }: UserStateModel): UserStateModel['user'] {
    return user;
  }

  /**
   * If multiple roles are provided, returns true if at least one role is valid
   * @param role
   */
  static hasRole(role: UserRole | UserRole[]): (state: UserStateModel) => boolean {
    if (!Array.isArray(role)) {
      role = [role];
    }

    return createSelector([UserState], (state: UserStateModel) => {
      const user = state?.user;
      if (user === null || user === 'loading') {
        return false;
      }
      return role.some(r => user.role === r);
    });
  }

  static hasAccess(role: UserRole): (state: UserStateModel) => boolean {
    return createSelector([UserState], (state: UserStateModel) => {
      const user = state?.user;
      return (
        user !== null &&
        user !== 'loading' &&
        UserRoles.includes(user.role) &&
        UserRoles.indexOf(role) >= UserRoles.indexOf(user.role)
      );
    });
  }

  @Selector([UserState])
  static getUsername(state: UserStateModel): string | null {
    const user = state?.user;
    return user !== null && user !== 'loading' ? user.name : null;
  }

  @Selector([UserState])
  static getUserId(state: UserStateModel): string | null {
    const user = state?.user;
    return user !== null && user !== 'loading' ? user.id : null;
  }

  @Selector([UserState])
  static getCheckInQRCodeFileId(state: UserStateModel): string | null {
    const user = state?.user;
    return user !== null && user !== 'loading' && user.checkInQrCodeFileId !== undefined
      ? user.checkInQrCodeFileId
      : null;
  }

  @Selector([UserState])
  static isSuperAdmin(state: UserStateModel): boolean {
    return UserStateSelectors.hasRole('SuperAdmin')(state);
  }

  @Selector([UserState])
  static isAdmin(state: UserStateModel): boolean {
    return UserStateSelectors.hasRole('Admin')(state);
  }

  @Selector([UserState])
  static isVipGold(state: UserStateModel): boolean {
    return UserStateSelectors.hasRole('VipGold')(state);
  }

  @Selector([UserState])
  static isVipSilver(state: UserStateModel): boolean {
    return UserStateSelectors.hasRole('VipSilver')(state);
  }
}
